import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '../config/api';
import ShareLearningModal from '../components/ShareLearningModal';
import { FaShare, FaHeart, FaEye, FaTag } from 'react-icons/fa';

interface ProgressData {
  date: string;
  wordsLearned: number;
  timeSpent: number;
  accuracy: number;
}

interface CategoryProgress {
  category: string;
  totalWords: number;
  masteredWords: number;
  percentage: number;
}

interface OverallStats {
  totalWordsLearned: number;
  totalStudyTime: number;
  currentStreak: number;
  averageAccuracy: number;
}

interface ProgressResponse {
  dailyProgress: ProgressData[];
  categoryProgress: CategoryProgress[];
  overallStats: OverallStats;
}

const Progress: React.FC = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'year'>('week');
  const [showShareModal, setShowShareModal] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Demo data for non-authenticated users
  const demoData: ProgressResponse = {
    dailyProgress: [
      { date: '2024-01-15', wordsLearned: 12, timeSpent: 45, accuracy: 85 },
      { date: '2024-01-16', wordsLearned: 8, timeSpent: 30, accuracy: 92 },
      { date: '2024-01-17', wordsLearned: 15, timeSpent: 60, accuracy: 78 },
      { date: '2024-01-18', wordsLearned: 10, timeSpent: 40, accuracy: 88 },
      { date: '2024-01-19', wordsLearned: 20, timeSpent: 75, accuracy: 95 },
      { date: '2024-01-20', wordsLearned: 14, timeSpent: 50, accuracy: 82 },
      { date: '2024-01-21', wordsLearned: 18, timeSpent: 65, accuracy: 90 }
    ],
    categoryProgress: [
      { category: 'Greetings', totalWords: 25, masteredWords: 20, percentage: 80 },
      { category: 'Numbers', totalWords: 30, masteredWords: 25, percentage: 83 },
      { category: 'Family', totalWords: 20, masteredWords: 15, percentage: 75 },
      { category: 'Food', totalWords: 35, masteredWords: 28, percentage: 80 },
      { category: 'Travel', totalWords: 40, masteredWords: 30, percentage: 75 }
    ],
    overallStats: {
      totalWordsLearned: 150,
      totalStudyTime: 365,
      currentStreak: 7,
      averageAccuracy: 87
    }
  };

  // Fetch progress data from API only if user is authenticated
  const { data: progressData, isLoading, error } = useQuery<ProgressResponse>({
    queryKey: ['userProgress'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Fetching progress data from:', `${API_BASE_URL}/api/progress`);
      
      const response = await fetch(`${API_BASE_URL}/api/progress`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Progress API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Progress API error:', errorText);
        throw new Error(`Failed to fetch progress data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Progress API response:', result);
      return result.data;
    },
    enabled: !!user,
    refetchInterval: 300000, // Refetch every 5 minutes
    retry: 3,
    retryDelay: 1000,
  });

  // Use demo data if user is not authenticated, otherwise use real data
  const displayData = user ? progressData : demoData;

  // If user is not authenticated, show a message to sign in
  if (!user) {
    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold dark:text-white mb-4">
            Learning Progress 📈
          </h1>
          <p className="text-lg dark:text-gray-100">
            Track your progress and learning statistics
          </p>
        </div>

        {/* Sign in prompt */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-8 text-center">
          <div className="text-blue-600 dark:text-blue-400 text-4xl mb-4">🔐</div>
          <h3 className="text-xl font-semibold dark:text-white mb-4">
            Sign in to view your progress
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            To track your learning progress and statistics, please sign in to your account
          </p>
          <div className="flex justify-center space-x-4 rtl:space-x-reverse">
            <a
              href="/login"
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
            >
              Sign In
            </a>
            <a
              href="/register"
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
            >
              Register
            </a>
          </div>
        </div>

        {/* Demo data notice */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold dark:text-white mb-4">
            Sample Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Below you can see a sample of progress data
          </p>
          
          {/* Show demo data */}
          <div className="space-y-6">
            {/* Overall Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📚</span>
                  </div>
                  <div className="mr-4">
                    <p className="text-sm dark:text-gray-100">
                      Words Learned
                    </p>
                    <p className="text-2xl font-bold dark:text-white">{demoData.overallStats.totalWordsLearned}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">⏰</span>
                  </div>
                  <div className="mr-4">
                    <p className="text-sm dark:text-gray-100">
                      Study Time
                    </p>
                    <p className="text-2xl font-bold dark:text-white">{demoData.overallStats.totalStudyTime} minutes</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🎯</span>
                  </div>
                  <div className="mr-4">
                    <p className="text-sm dark:text-gray-100">
                      Average Accuracy
                    </p>
                    <p className="text-2xl font-bold dark:text-white">{demoData.overallStats.averageAccuracy}%</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🔥</span>
                  </div>
                  <div className="mr-4">
                    <p className="text-sm dark:text-gray-100">
                      Streak Days
                    </p>
                    <p className="text-2xl font-bold dark:text-white">{demoData.overallStats.currentStreak}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    console.error('Progress page error:', error);
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold dark:text-white mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {error.message || 'Please try again'}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!displayData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-gray-500 text-xl mb-4">📊</div>
          <h3 className="text-lg font-semibold dark:text-white mb-2">
            No Data Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Start learning to see your progress
          </p>
        </div>
      </div>
    );
  }

  const { dailyProgress, categoryProgress, overallStats } = displayData;

  const getTotalWordsLearned = () => {
    return overallStats.totalWordsLearned;
  };

  const getTotalTimeSpent = () => {
    return overallStats.totalStudyTime;
  };

  const getAverageAccuracy = () => {
    return overallStats.averageAccuracy;
  };

  const getStreakDays = () => {
    return overallStats.currentStreak;
  };

  const handleShareLearning = async (shareData: any) => {
    if (!user) return;
    
    setIsSharing(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/shared-learning`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(shareData)
      });

      if (response.ok) {
        setShowShareModal(false);
        // You could show a success message here
        alert('Your learning progress has been shared successfully!');
      } else {
        throw new Error('Failed to share learning progress');
      }
    } catch (error) {
      console.error('Error sharing learning progress:', error);
      alert('Failed to share learning progress. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold dark:text-white mb-4">
          Learning Progress 📈
        </h1>
        <p className="text-lg dark:text-gray-100">
          Track your progress and learning statistics
        </p>
        
        {/* Share Button */}
        {user && (
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowShareModal(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <FaShare className="mr-2" />
              Share My Learning
            </button>
            <a
              href="/shared-learning"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <FaEye className="mr-2" />
              View Shared Stories
            </a>
          </div>
        )}
      </div>



      {/* Period Selector */}
      <div className="flex justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-1 shadow-lg">
          <button
            onClick={() => setSelectedPeriod('week')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedPeriod === 'week'
                ? 'bg-orange-500 text-white'
                : 'dark:text-gray-100 hover:text-orange-500 dark:hover:text-orange-400'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setSelectedPeriod('month')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedPeriod === 'month'
                ? 'bg-orange-500 text-white'
                : 'dark:text-gray-100 hover:text-orange-500 dark:hover:text-orange-400'
            }`}
          >
            Month
          </button>
          <button
            onClick={() => setSelectedPeriod('year')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedPeriod === 'year'
                ? 'bg-orange-500 text-white'
                : 'dark:text-gray-100 hover:text-orange-500 dark:hover:text-orange-400'
            }`}
          >
            Year
          </button>
        </div>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📚</span>
            </div>
            <div className="mr-4">
              <p className="text-sm dark:text-gray-100">
                Words Learned
              </p>
              <p className="text-2xl font-bold dark:text-white">{getTotalWordsLearned()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <span className="text-2xl">⏰</span>
            </div>
            <div className="mr-4">
              <p className="text-sm dark:text-gray-100">
                Study Time
              </p>
              <p className="text-2xl font-bold dark:text-white">{getTotalTimeSpent()} minutes</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🎯</span>
            </div>
            <div className="mr-4">
              <p className="text-sm dark:text-gray-100">
                Average Accuracy
              </p>
              <p className="text-2xl font-bold dark:text-white">{getAverageAccuracy()}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🔥</span>
            </div>
            <div className="mr-4">
              <p className="text-sm dark:text-gray-100">
                Streak Days
              </p>
              <p className="text-2xl font-bold dark:text-white">{getStreakDays()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
        <h2 className="text-2xl font-bold dark:text-white mb-6">
          Daily Progress
        </h2>
        <div className="space-y-4">
          {dailyProgress.length > 0 ? (
            dailyProgress.map((day, index) => (
              <div key={index} className="flex items-center space-x-4 rtl:space-x-reverse">
                <div className="w-20 text-sm dark:text-gray-100">
                  {new Date(day.date).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric' })}
                </div>
                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-red-500 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((day.wordsLearned / 10) * 100, 100)}%` }}
                  ></div>
                </div>
                <div className="w-16 text-sm dark:text-gray-100 text-center">
                  {day.wordsLearned} words
                </div>
                <div className="w-16 text-sm dark:text-gray-100 text-center">
                  {day.timeSpent} min
                </div>
                <div className="w-16 text-sm dark:text-gray-100 text-center">
                  {day.accuracy}%
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 text-2xl mb-4">📊</div>
              <p className="text-gray-600 dark:text-gray-400">
                No activity recorded yet
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Category Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
        <h2 className="text-2xl font-bold dark:text-white mb-6">
          Category Progress
        </h2>
        <div className="space-y-6">
          {categoryProgress.length > 0 ? (
            categoryProgress.map((category, index) => (
              <div key={index}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium dark:text-white">{category.category}</span>
                  <span className="text-sm dark:text-gray-100">
                    {category.masteredWords} of {category.totalWords}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${category.percentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs dark:text-gray-100">
                    {Math.round(category.percentage)}% completed
                  </span>
                  <span className="text-xs dark:text-gray-100">
                    {category.totalWords - category.masteredWords} words remaining
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 text-2xl mb-4">📂</div>
              <p className="text-gray-600 dark:text-gray-400">
                No categories started yet
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Achievements */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
        <h2 className="text-2xl font-bold dark:text-white mb-6">
          Achievements 🏆
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`text-center p-4 rounded-lg ${
            getTotalWordsLearned() > 0 
              ? 'bg-orange-50 dark:bg-orange-900/20' 
              : 'bg-gray-50 dark:bg-gray-700/20'
          }`}>
            <div className="text-4xl mb-2">🎯</div>
            <h3 className="font-semibold dark:text-white mb-1">Beginner</h3>
            <p className="text-sm dark:text-gray-100">Learned your first word</p>
          </div>
          <div className={`text-center p-4 rounded-lg ${
            getStreakDays() >= 7 
              ? 'bg-green-50 dark:bg-green-900/20' 
              : 'bg-gray-50 dark:bg-gray-700/20'
          }`}>
            <div className="text-4xl mb-2">🔥</div>
            <h3 className="font-semibold dark:text-white mb-1">Enthusiast</h3>
            <p className="text-sm dark:text-gray-100">7 consecutive days of study</p>
          </div>
          <div className={`text-center p-4 rounded-lg ${
            getTotalWordsLearned() >= 50 
              ? 'bg-blue-50 dark:bg-blue-900/20' 
              : 'bg-gray-50 dark:bg-gray-700/20'
          }`}>
            <div className="text-4xl mb-2">📚</div>
            <h3 className="font-semibold dark:text-white mb-1">Scholar</h3>
            <p className="text-sm dark:text-gray-100">Learned 50 words</p>
          </div>
        </div>
      </div>

      {/* Study Recommendations */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-8 text-white">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-4">
            Study Recommendations 📝
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="text-center">
              <div className="text-3xl mb-2">🎯</div>
              <h4 className="font-semibold mb-2">Focus on Weak Areas</h4>
              <p className="text-blue-100 text-sm">
                Focus on categories you're less proficient in
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">⏰</div>
              <h4 className="font-semibold mb-2">Regular Schedule</h4>
              <p className="text-blue-100 text-sm">
                Study at a specific time every day
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🔄</div>
              <h4 className="font-semibold mb-2">Regular Review</h4>
              <p className="text-blue-100 text-sm">
                Regularly review previous words
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Share Learning Modal */}
      <ShareLearningModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShareLearning}
        isLoading={isSharing}
      />
    </div>
  );
};

export default Progress; 